import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {
  DeletePendingOrganizationMember,
  DeletePendingOrganizationMemberComplete,
  DeletePendingOrganizationMemberError,
  DeletePendingOrganizationUser,
  DeletePendingOrganizationUserComplete,
  DeletePendingOrganizationUserError,
  GetOrganizationUsers,
  GetOrganizationUsersComplete,
  GetOrganizationUsersError,
  GetSelfOrganizationUser,
  GetSelfOrganizationUserComplete,
  GetSelfOrganizationUserError,
  OrganizationUserActionTypes,
  PostGroupInvitation,
  PostGroupInvitationComplete,
  PostGroupInvitationError,
  PostOrganizationInvitation,
  PostOrganizationInvitationComplete,
  PostOrganizationInvitationError
} from '../actions/organization-user.actions';
import {catchError, exhaustMap, map} from 'rxjs/operators';
import {forkJoin, of} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {
  DeletePendingOrganizationMemberRequest,
  DeletePendingOrganizationUserRequest,
  GetOrganizationUsersRequest,
  GetSelfOrganizationUserRequest,
  OrganizationUser,
  PostGroupInvitationRequest,
  PostOrganizationInvitationRequest,
  SelfOrganizationUser
} from '../models/organization-user';
import {OrganizationUserService} from '../services/organization-user.service';
import {Simple200Response} from '../models/general';
import {mergeMap} from 'rxjs/internal/operators';

@Injectable()
export class OrganizationUserEffects {

  @Effect()
  getOrganizationUsers$ = this.actions$.pipe(
    ofType(OrganizationUserActionTypes.GetOrganizationUsers),
    map((action: GetOrganizationUsers) => action.payload),
    exhaustMap((getOrganizationUsersRequest: GetOrganizationUsersRequest) =>
      this.organizationUserService.getOrganizationUsers(getOrganizationUsersRequest)
        .pipe(
          map((organizationUsers: OrganizationUser[]) => new GetOrganizationUsersComplete(organizationUsers)),
          catchError((error: HttpErrorResponse) => of(new GetOrganizationUsersError({
            errorMessage: error.message
          })))
        )
    )
  );

  @Effect()
  getSelfOrganizationUser$ = this.actions$.pipe(
    ofType(OrganizationUserActionTypes.GetSelfOrganizationUser),
    map((action: GetSelfOrganizationUser) => action.payload),
    exhaustMap((getSelfOrganizationUserRequest: GetSelfOrganizationUserRequest) =>
      this.organizationUserService.getSelfOrganizationUser(getSelfOrganizationUserRequest)
        .pipe(
          map((selfOrganizationUser: SelfOrganizationUser) => new GetSelfOrganizationUserComplete(selfOrganizationUser)),
          catchError((error: HttpErrorResponse) => of(new GetSelfOrganizationUserError({
            errorMessage: error.message
          })))
        )
    )
  );

  @Effect()
  postOrganizationInvitation$ = this.actions$.pipe(
    ofType(OrganizationUserActionTypes.PostOrganizationInvitation),
    map((action: PostOrganizationInvitation) => action.payload),
    exhaustMap((postOrganizationInvitationRequest: PostOrganizationInvitationRequest) =>
      this.organizationUserService.postOrganizationInvitation(postOrganizationInvitationRequest)
        .pipe(
          map((response: Simple200Response) => new PostOrganizationInvitationComplete()),
          catchError((error: HttpErrorResponse) => of(new PostOrganizationInvitationError({
            errorMessage: error.message
          })))
        )
    )
  );

  @Effect()
  postGroupInvitation$ = this.actions$.pipe(
    ofType(OrganizationUserActionTypes.PostGroupInvitation),
    map((action: PostGroupInvitation) => action.payload),
    exhaustMap((postGroupInvitationRequest: PostGroupInvitationRequest) =>
      this.organizationUserService.postGroupInvitation(postGroupInvitationRequest)
        .pipe(
          map((response: Simple200Response) => new PostGroupInvitationComplete()),
          catchError((error: HttpErrorResponse) => of(new PostGroupInvitationError({
            errorMessage: error.message
          })))
        )
    )
  );

  @Effect()
  deletePendingOrganizationUser$ = this.actions$.pipe(
    ofType(OrganizationUserActionTypes.DeletePendingOrganizationUser),
    map((action: DeletePendingOrganizationUser) => action.payload),
    exhaustMap((deletePendingOrganizationUserRequest: DeletePendingOrganizationUserRequest) =>
      forkJoin(of(deletePendingOrganizationUserRequest.organizationToken),
        this.organizationUserService.deletePendingOrganizationUser(deletePendingOrganizationUserRequest))
        .pipe(
          mergeMap(([organizationToken, response]: [string, Simple200Response]) => [
            new GetOrganizationUsers({organizationToken}),
            new DeletePendingOrganizationUserComplete()
          ]),
          catchError((error: HttpErrorResponse) => of(new DeletePendingOrganizationUserError({
            errorMessage: error.message
          })))
        )
    )
  );

  @Effect()
  deletePendingOrganizationMember$ = this.actions$.pipe(
    ofType(OrganizationUserActionTypes.DeletePendingOrganizationMember),
    map((action: DeletePendingOrganizationMember) => action.payload),
    exhaustMap((deletePendingOrganizationMemberRequest: DeletePendingOrganizationMemberRequest) =>
      forkJoin(of(deletePendingOrganizationMemberRequest.organizationToken),
        this.organizationUserService.deletePendingOrganizationMember(deletePendingOrganizationMemberRequest))
        .pipe(
          mergeMap(([organizationToken, response]: [string, Simple200Response]) => [
            new GetOrganizationUsers({organizationToken}),
            new DeletePendingOrganizationMemberComplete()
          ]),
          catchError((error: HttpErrorResponse) => of(new DeletePendingOrganizationMemberError({
            errorMessage: error.message
          })))
        )
    )
  );

  constructor(private actions$: Actions,
              private organizationUserService: OrganizationUserService) {
  }
}
